/**
 * 파일명 : pjt0105m.jsx
 * 설명 : 외부직원 내역 조회/등록 화면
 * 수정내역
 ************************************************
 * Version	   수정일자			수정자	    수정내용
 ************************************************
 * 1.0		  2023-03-30	   윤하영	   최초작성
 **/

/************************************************
 * Import 영역
 ************************************************/
/* React */
import React from "react";
import locale from "antd/lib/locale/ko_KR";

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Pjt0105mAction } from '../../../redux/slice/pjt/pjt0105m_slice';
import { IInquiryCndsState, IExtUserItemState, IExtUserListItemState, IPostCdState, IBelongDcdState } from '../../../redux/models/pjt/pjt0105m_model';

/*  AntD  */
import { Button, Space, Row, Col, Layout, Typography, Collapse, Table, Select, Input, Radio, Descriptions, ConfigProvider, DatePicker } from "antd";
import { SearchOutlined, CaretRightOutlined } from "@ant-design/icons";

/* 업무 */
import * as CommonProxy from "libs/CommonProxy";
import * as common from "libs/common";
import dayjs from 'dayjs';

/************************************************
 * 전역변수 영역
 ************************************************/
const Pjt0105m = () => {
    /************************************************
     * 화면 초기화 영역
     ************************************************/

    /**
     * 기능 : state 초기 구조선언
     * 설명 : state 초기 구조선언
     */
    const dispatch = useAppDispatch();
    const inquiryCnds = useAppSelector((state) => state.Pjt0105m.inquiryCnds);
    const extUserData = useAppSelector((state) => state.Pjt0105m.extUserData);
    const extUserList = useAppSelector((state) => state.Pjt0105m.extUserList);
    const belongDcdList = useAppSelector((state) => state.Pjt0105m.belongDcdList);
    const postCdList = useAppSelector((state) => state.Pjt0105m.postCdList);
    const pageSize = useAppSelector((state) => state.Pjt0105m.pageSize);
    const current = useAppSelector((state) => state.Pjt0105m.current);

    /* Render 변수 선언영역 */
    /* AntD */
    const { Content } = Layout;
    const { Text } = Typography;
    const { Panel } = Collapse;

    const [showAutoComplete, setShowAutoComplete] = React.useState(false);
    const [autoCompleteOptions, setAutoCompleteOptions] = React.useState([]);

    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     */
    React.useEffect(() => {
        setPostCdList();
        setBelongDcdList();

        fn_Reset();
        fn_Search();

    }, []);

    /**
     * 기능 : setPostCdList
     * 설명 : 직위코드
     * param : 
     * return : None
     **/
    const setPostCdList = () => {
        const option = {
            callback: (datas: IPostCdState[]) => {
                let arrOptions: IPostCdState[] = [];

                arrOptions.push({
                    label: '선택',
                    value: '',
                });

                datas.forEach((item: IPostCdState) => {
                    arrOptions.push({
                        label: item.cdNm,
                        value: item.cd,
                    });
                });

                dispatch(Pjt0105mAction.setPostCdList(arrOptions));
            }

        };
        common.getComCodeList('011', option);
    }

    /**
     * 기능 : setBelongDcdList
     * 설명 : 소속코드
     * param : 
     * return : None
     **/
    const setBelongDcdList = () => {
        const option = {
            callback: (datas: IBelongDcdState[]) => {
                let arrOptions: IBelongDcdState[] = [];

                arrOptions.push({
                    label: '전체',
                    value: '',
                });

                datas.forEach((item: IBelongDcdState) => {
                    arrOptions.push({
                        label: item.cdNm,
                        value: item.cd,
                    });
                });

                arrOptions = arrOptions.filter(item => item.value !== '10');

                dispatch(Pjt0105mAction.setBelongDcdList(arrOptions));  // 소속코드 10(직원) 제거
            }

        };
        common.getComCodeList('029', option);

    };

    /* Table Columns */
    const columns = React.useCallback(() => {
        const arr: any = [
            {
                title: () => <div style={{ textAlign: "center" }}>성명</div>,
                dataIndex: "userNm",
                key: "userNm",
                align: "center",
                width: "10%",
                // render: (_: any, record: any) => (
                //     <Button
                //         className="link-button"
                //         size="small"
                //         onClick={() => showDetail(record)}
                //     >
                //         {record.userNm}
                //     </Button>
                // ), // eslint-disable-line no-dupe-keys
            },
            {
                title: () => <div style={{ textAlign: "center" }}>회원 ID</div>,
                dataIndex: "extEmpNo",
                key: "extEmpNo",
                align: "center",
                width: "10%",
                // render: (_: any, record: any) => (
                //     <Button
                //         className="link-button"
                //         size="small"
                //         onClick={() => showDetail(record)}
                //     >
                //         {record.extEmpNo}
                //     </Button>
                // ), // eslint-disable-line no-dupe-keys
            },
            {
                title: () => <div style={{ textAlign: "center" }}>이메일</div>,
                dataIndex: "userEmail",
                key: "userEmail",
                align: "center",
                width: "15%",
            },
            {
                title: () => <div style={{ textAlign: "center" }}>전화번호</div>,
                dataIndex: "mobileNo",
                key: "mobileNo",
                align: "center",
                width: "10%",
            },
            {
                title: () => <div style={{ textAlign: "center" }}>직위</div>,
                dataIndex: "postNm",
                key: "postNm",
                align: "center",
                width: "10%",
            },
            {
                title: () => <div style={{ textAlign: "center" }}>생년월일</div>,
                dataIndex: "pid",
                key: "pid",
                align: "center",
                width: "10%",
                render: (record: string) => dayjs(record.endsWith('1') || record.endsWith('2') ? '19' + record.slice(0, -1)
                    : record.endsWith('3') || record.endsWith('4') ? '20' + record.slice(0, -1)
                        : record).format("YYYY-MM-DD"),
            },
            {
                title: () => <div style={{ textAlign: "center" }}>성별</div>,
                dataIndex: "gndrTpNm",
                key: "gndrTpNm",
                align: "center",
                width: "10%",
            },
            {
                title: () => <div style={{ textAlign: "center" }}>소속</div>,
                dataIndex: "belongDnm",
                key: "belongDnm",
                align: "center",
                width: "10%",
            },];

        return arr;
    }, []);

    /**
     * 기능 : 상세보기(그리드선택시)
     * param :
     */
    const showDetail = (record: IExtUserListItemState) => {
        dispatch(Pjt0105mAction.setExtUserData({
            userNm: record.userNm,
            extEmpNo: record.extEmpNo,
            userEmail: record.userEmail,
            mobileNo: record.mobileNo,
            postCd: record.postCd,
            pid: record.pid.endsWith('1') || record.pid.endsWith('2') ? '19' + record.pid.slice(0, -1)
                : record.pid.endsWith('3') || record.pid.endsWith('4') ? '20' + record.pid.slice(0, -1)
                    : record.pid,
            gndrTpCd: record.gndrTpNm === "남" ? "01" : "02", // 성별을 "남"/"여"로 변환하여 코드 값 설정
            belongDcd: record.belongDcd,
            delYn: "N"
        }));
    };

    /************************************************
     * 이벤트 함수 영역
     ************************************************/
    /**
      * 기능 : 버튼 클릭 이벤트
      * 설명 : 등록/수정/삭제 확인
      * param : 
      * return : None
      */
    const btn_onClick = (btnNm: string, delYn: string) => {
        /* 정합성 체크 */
        if (!fn_CheckValidation(btnNm)) {
            return;
        }

        switch (btnNm) {
            case "btnSave": {
                const param = {
                    title: '위드정보 확인',
                    contents: '저장하시겠습니까?',
                    accept: () => {
                        btn_confirm(delYn);
                    }
                };
                common.openConfirm(param);
                break;
            }

            case "btnEdit": {
                const param = {
                    title: '위드정보 확인',
                    contents: '수정하시겠습니까?',
                    accept: () => {
                        btn_confirm(delYn);
                    }
                };
                common.openConfirm(param);
                break;
            }

            case "btnDelete": {
                const param = {
                    title: '위드정보 확인',
                    contents: '삭제하시겠습니까?',
                    accept: () => {
                        btn_confirm(delYn);
                    }
                };
                common.openConfirm(param);
                break;
            }

        }



    };
    /**
     * 기능 : 등록/수정/삭제버튼 클릭 이벤트
     * 설명 : 등록/수정/삭제
     * param :
     * return :
     */
    const btn_confirm = (delYn: string) => {
        const pid = extUserData.pid.slice(2);

        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = { ...extUserData, pid: pid, delYn: delYn };

        /* 트렌젝션 호출 및 결과 셋팅 */
        CommonProxy.callAsyncSvc(requestData, {
            txCd: 'TRPJTS0004',
            success: (responseData: any) => {
                dispatch(Pjt0105mAction.setExtUserData(responseData.extUser));

                const param = {
                    title: '위드정보 확인',
                    contents: '정상적으로 처리되었습니다.',
                    accept: () => {
                        fn_Reset();
                        fn_Search();
                    }
                };

                common.openAlert(param);
            },
        });
    };


    /**
     * 기능 : 초기화 함수
     * param :
     **/
    const fn_Reset = () => {
        dispatch(Pjt0105mAction.initState());
    };

    /**
     * 기능 : 결과초기화 이벤트 선언
     * param :
     */
    const fn_ResetResult = () => {
        dispatch(Pjt0105mAction.setExtUserList([]));
    };

    /**
     *  기능 : 
     *  설명 : 변경값을 받는다
     *  param :
     *  return : None
     */
    const handleChange = (field: string, value: string) => {
        dispatch(Pjt0105mAction.setExtUserData({ ...extUserData, [field]: value }));
    };

    /**
     *  기능 : RangePicker 데이터 변경
     *  설명 : RangePicker 데이터 변경값을 받는다
     *  param :
     *  return : None
     */
    const calendar_onChange = (_: any, dateStrings: any) => {
        dispatch(Pjt0105mAction.setExtUserData({ ...extUserData, pid: dayjs(dateStrings).format("YYYYMMDD") }));
    };

    /**
     * 기능 : 이메일 자동완성
     * 설명 : 
     * param :
     * return : None
     */
    const email_onChange = (event: any) => {
        const userEmail = event.target.value;

        const FrequencyEmails = [
            "@withinfo.com",
            "@naver.com",
            "@gmail.com",
            "@daum.net",
        ];

        // 한글이 포함된 경우, 이전 값으로 대체합니다.
        // if (/[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/.test(userEmail)) {
        //     event.target.value = extUserData.userEmail;
        //     return;
        // }

        // 입력 필드에서 "@" 문자가 입력될 때마다 자동완성 리스트를 보여줍니다.
        if (userEmail.includes("@")) {
            const [username, domain] = userEmail.split("@");
            const matchedOptions = FrequencyEmails
                .filter((option) => option.startsWith(`@${domain}`))
                .map((option) => option.replace(`@${domain}`, ""));

            setShowAutoComplete(true);
            setAutoCompleteOptions(matchedOptions);
        } else {
            setShowAutoComplete(false);
            setAutoCompleteOptions([]);
        }

        handleChange('userEmail', userEmail);
    };

    const email_onClick = (option: any) => {
        const newEmail = `${extUserData.userEmail}${option}`;
        handleChange('userEmail', newEmail);

        setShowAutoComplete(false);
        setAutoCompleteOptions([]);
    };


    /**
     * 기능 : 전화번호 010-0000-0000 형식으로 변경
     * 설명 : 
     * param :
     * return : None
     */
    const mobileNo_onChange = (event: any) => {
        const rawValue = event.target.value.replace(/-/g, "");
        const formattedValue = rawValue
            .replace(/[^\d]/g, "")
            .replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3")
            .replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3")
            .substring(0, 13);

        event.currentTarget.value = formattedValue;
        handleChange('mobileNo', formattedValue);
    };

    /**
     * 기능 : onClick 이벤트 선언
     * param :
     */
    const onClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            case "btnSearch": {
                fn_ResetResult();
                fn_Search();
                break;
            }

            case "btnReset": {
                fn_Reset();
                break;
            }

            case "btnDelete": {
                btn_onClick(buttonName, "Y");
                break;
            }
            case "btnEdit": {
                btn_onClick(buttonName, "N");
                break;
            }

            case "btnSave": {
                btn_onClick(buttonName, "N");
                break;
            }

            default:
        }
    };

    /**
     * 기능 : 검색조회
     * param :
     */
    const fn_Search = () => {
        dispatch(Pjt0105mAction.setCurrent(1));

        // 입력파라미터
        const requestData: IInquiryCndsState = {
            ...inquiryCnds,
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        CommonProxy.callAsyncSvc(requestData, {
            txCd: 'TRPJTR0005',
            success: (responseData: any) => {
                dispatch(Pjt0105mAction.setExtUserList(responseData.extUserList));
            },
        });
    };

    /**
     * 기능 : 입력 후 Enter key 입력 시 조회
     * 설명 : 입력컴포넌트
     * param : 
     * return : None
     */
    const handleOnKeyUp = (e: any) => {
        if (e.key === 'Enter') {
            fn_Search(); // 조회
        }
    };

    /************************************************
     * 개발자함수 영역
     ************************************************/
    /**
     * 기능 : 정합성체크
     * 설명 : 정합성체크
     * param : 
     * return : None
     */
    const fn_CheckValidation = (buttonName: string) => {

        const result = true;
        const requiredFields: Array<keyof typeof fieldNames> = [
            "userNm",
            "belongDcd",
            "postCd",
            "gndrTpCd",
            "userEmail",
            "mobileNo",
        ];

        const fieldNames = {
            userNm: "성명",
            belongDcd: "소속",
            postCd: "직위",
            pid: "생년월일",
            gndrTpCd: "성별",
            userEmail: "이메일",
            mobileNo: "전화번호",
        };

        for (const field of requiredFields) {
            // extUserData[field]의 값이 비어있는지 체크
            if (common.isEmptyString(extUserData[field])) {
                const param = {
                    title: '위드정보 확인',
                    contents: `${fieldNames[field]}을(를) 입력해주세요.`,
                };
                common.openInfo(param);
                return false;
            }
        }

        if (buttonName === "btnDelete") {
            if (common.isEmptyString(extUserData.extEmpNo)) {
                const param = {
                    title: '위드정보 확인',
                    contents: '회원 ID는 필수입력 항목입니다.',
                };
                common.openInfo(param);
                return false;
            }
            return result;

        }

        if (buttonName === "btnEdit" || buttonName === "btnDelete") {
            console.log()
            if (common.isEmptyString(extUserData.extEmpNo)) {
                const param = {
                    title: '위드정보 확인',
                    contents: '회원 ID는 필수입력 항목입니다.',
                };
                common.openInfo(param);
                return false;
            }


            return result;
        }

        return result;
    }

    return (
        /* 화면 디자인 영역 */
        <Layout>
            <Content>
                <Collapse
                    bordered={false}
                    defaultActiveKey={["1"]}
                    expandIcon={({ isActive }) =>
                        <CaretRightOutlined rotate={isActive ? 90 : 0} />
                    }
                    className="site-collapse-custom-collapse"
                >
                    <Panel
                        header="검색조건"
                        key="1"
                        className="site-collapse-custom-panel"
                    >
                        <Row
                            align="middle"
                            justify="space-between"
                            style={{ margin: 10 }}
                        >
                            <Col >
                                <Space>
                                    <label>외부직원 조회</label>
                                    <Input
                                        placeholder="검색할 이름을 입력하세요"
                                        style={{ width: 400 }}
                                        name="userNm"
                                        onKeyUp={handleOnKeyUp}
                                        onChange={e => { dispatch(Pjt0105mAction.setInquiryCnds({ ...inquiryCnds, userNm: e.currentTarget.value })) }}
                                        value={inquiryCnds.userNm}
                                    />
                                </Space>
                            </Col>
                            <Col>
                                <Space>
                                    <label>소속</label>
                                    <Select
                                        style={{ width: 200, }}
                                        optionFilterProp='label'
                                        options={belongDcdList}
                                        onKeyUp={handleOnKeyUp}
                                        onChange={val => {
                                            dispatch(Pjt0105mAction.setInquiryCnds({ ...inquiryCnds, belongDcd: val }))
                                        }}
                                        value={inquiryCnds.belongDcd}
                                    />
                                </Space>
                            </Col>
                            <Col>
                                <Space>
                                    <Button
                                        name="btnReset"
                                        className="background-color-default-button"
                                        onClick={onClick}
                                    >
                                        초기화
                                    </Button>
                                    <Button
                                        name="btnSearch"
                                        className="background-color-primary-button"
                                        icon={<SearchOutlined />}
                                        onClick={onClick}
                                    >
                                        조회
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Panel>
                </Collapse>
                <Row align="middle" style={{ height: 50 }}>
                    <Col flex='9.5' >
                        <Space>
                            <img className="title-icon" alt="" />
                            <Text strong>외부직원 목록</Text>
                        </Space>
                    </Col>
                </Row>
                <Row>
                    <Table dataSource={extUserList}
                        key='no'
                        columns={columns()}
                        size='small'
                        style={{ width: '100%' }}
                        scroll={{ y: 550 }}
                        pagination={{
                            pageSize: pageSize,
                            current: current,
                            showTotal: () => '총 ' + extUserList.length + ' 건',
                            onChange: (page, pageSize) => {
                                dispatch(Pjt0105mAction.setCurrent(page));
                                dispatch(Pjt0105mAction.setPageSize(pageSize));
                            },
                        }}
                        onRow={(record) => ({
                            onClick: () => showDetail(record), 
                        })}
                    />
                </Row>
                <Row>
                    <Col style={{ width: "100%" }}>
                        <Space>
                            <img className="title-icon" alt="" />
                            <Text strong>외부직원 등록</Text>
                        </Space>
                        <Descriptions
                            bordered
                            column={3}
                            style={{ marginTop: 20 }}
                            labelStyle={{ width: "10%", textAlign: "center" }}
                        >
                            <Descriptions.Item label="성명">
                                <Input
                                    placeholder="ex) 홍길동"
                                    style={{ width: "100%" }}
                                    name="userNm"
                                    value={extUserData.userNm}
                                    onChange={(e) => handleChange('userNm', e.currentTarget.value)}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label="회원 ID">
                                <Space>{extUserData.extEmpNo}</Space>
                            </Descriptions.Item>
                            <Descriptions.Item label="소속">
                                <Select
                                    style={{ width: '100%' }}
                                    optionFilterProp='label'
                                    options={belongDcdList}
                                    value={extUserData.belongDcd}
                                    disabled={!common.isAdmin()}
                                    onChange={(val) => handleChange('belongDcd', val)}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label="직위">
                                <Select
                                    style={{ width: '100%' }}
                                    optionFilterProp='label'
                                    options={postCdList}
                                    value={extUserData.postCd}
                                    disabled={!common.isAdmin()}
                                    onChange={(val) => handleChange('postCd', val)}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label="생년월일">
                                <ConfigProvider locale={locale}>
                                    <DatePicker
                                        style={{ width: "100%" }}
                                        allowClear={false}
                                        onChange={calendar_onChange}
                                        picker='date'
                                        value={dayjs(extUserData.pid)}
                                    />
                                </ConfigProvider>
                            </Descriptions.Item>
                            <Descriptions.Item label="성별">
                                <Radio.Group
                                    value={extUserData.gndrTpCd}
                                    name="extUser.gndrTpCd"
                                    onChange={(e) => handleChange('gndrTpCd', e.target.value)}
                                    style={{ marginLeft: 20 }}
                                >
                                    <Radio key="gndrTpCd_M" value="01">
                                        남&nbsp;&nbsp;&nbsp;&nbsp;
                                    </Radio>
                                    <Radio key="gndrTpCd_F" value="02">
                                        여
                                    </Radio>
                                </Radio.Group>
                            </Descriptions.Item>
                            <Descriptions.Item
                                label="이메일"
                                style={{ position: "relative" }}
                            >
                                <div>
                                    <Input
                                        placeholder="ex) admin@withinfo.com"
                                        style={{ width: "100%" }}
                                        name="userEmail"
                                        value={extUserData.userEmail}
                                        onChange={email_onChange}
                                    />
                                    {showAutoComplete && (
                                        <div
                                            style={{
                                                width: "90%",
                                                position: "absolute",
                                                backgroundColor: "white",
                                                zIndex: 1,
                                            }}
                                        >
                                            {autoCompleteOptions.map((option) => (
                                                <div
                                                    style={{
                                                        padding: 4,
                                                        paddingLeft: 10,
                                                        borderWidth: "1px",
                                                        borderStyle: "solid",
                                                        borderColor: "#d9d9d9",
                                                    }}
                                                    key={option}
                                                    onClick={() => email_onClick(option)}
                                                // onMouseEnter={(e) => {
                                                //     e.target.style.backgroundColor = "#f5f5f5";
                                                // }}
                                                // onMouseLeave={(e) => {
                                                //     e.target.style.backgroundColor = "#ffffff";
                                                // }}
                                                >
                                                    {`${extUserData.userEmail}${option}`}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </Descriptions.Item>
                            <Descriptions.Item label="전화번호">
                                <Input
                                    placeholder="ex) 010-1234-5678"
                                    style={{ width: "40%" }}
                                    name="mobileNo"
                                    value={extUserData.mobileNo}
                                    onChange={mobileNo_onChange}
                                />
                            </Descriptions.Item>
                        </Descriptions>
                    </Col>
                </Row>

                <Row justify="space-around" style={{ marginTop: 20 }}>
                    <Col style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
                        <Space>
                            <Button
                                name="btnReset"
                                className="background-color-default-button"
                                onClick={onClick}
                            >
                                초기화
                            </Button>
                            <Button
                                name="btnEdit"
                                className="background-color-default-button"
                                onClick={onClick}
                                disabled={extUserData.extEmpNo === null || extUserData.extEmpNo === ""}
                            >
                                수정
                            </Button>
                            <Button
                                name="btnDelete"
                                className="background-color-default-button"
                                onClick={onClick}
                                disabled={extUserData.extEmpNo === null || extUserData.extEmpNo === ""}
                            >
                                삭제
                            </Button>
                            <Button
                                name="btnSave"
                                className="background-color-primary-button"
                                onClick={onClick}
                                disabled={extUserData.extEmpNo !== null && extUserData.extEmpNo !== ""}
                            >
                                저장
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Content>
        </Layout>
    );
};

export default Pjt0105m;
